import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthSession } from "../../auth/shared";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import {
  getCurrentUserProfile,
  getProfileErrorMessage,
} from "../api/profile.api";

interface UseCurrentUserProfileReturn {
  error: string | null;
  isLoading: boolean;
  isReady: boolean;
  refetch: () => Promise<void>;
}

export const useCurrentUserProfile = (): UseCurrentUserProfileReturn => {
  const { isLoggedIn, updateUser, user } = useAuthSession();
  const userRef = useRef(user);
  const updateUserRef = useRef(updateUser);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const { run: runLatestRequest, cancel, loading } = useLatestRequest();

  userRef.current = user;
  updateUserRef.current = updateUser;

  const fetchProfile = useCallback(async (): Promise<void> => {
    if (!userRef.current) {
      return;
    }

    setError(null);

    try {
      const profile = await runLatestRequest((signal) => getCurrentUserProfile(signal));

      if (profile === undefined) {
        return;
      }

      if (profile && userRef.current) {
        updateUserRef.current({ ...userRef.current, ...profile });
        setIsReady(true);
      }
    } catch (requestError: unknown) {
      setIsReady(false);
      setError(getProfileErrorMessage(requestError, "Failed to retrieve user profile"));
    }
  }, [runLatestRequest]);

  useEffect(() => {
    if (!isLoggedIn || !user?.id) {
      cancel();
      setError(null);
      setIsReady(false);
      return;
    }

    void fetchProfile();
  }, [cancel, fetchProfile, isLoggedIn, user?.id]);

  return { error, isLoading: loading, isReady, refetch: fetchProfile };
};
